/**
 * API Types
 *
 * Shared types for API requests and responses.
 */

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeout?: number;
  skipAuth?: boolean;
  skipCsrf?: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: Record<string, unknown>;

  constructor(message: string, status: number, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  static fromResponse(status: number, data: unknown): ApiRequestError {
    if (typeof data === 'object' && data !== null) {
      const errorData = data as Record<string, unknown>;
      // Prefer human-readable `message` over error code; use `error` as the code
      let message =
        (errorData.message as string) || (errorData.error as string) || 'Request failed';
      // The reporting query endpoint (and others using the same envelope)
      // returns ``exception_class`` alongside a generic ``error`` string
      // so prod doesn't leak SQL. The class name alone is safe to show
      // and turns "An unexpected error ran the query." into something
      // diagnosable ("FieldError", "ProgrammingError", "InvalidOperation")
      // without giving away schema details.
      const exceptionClass = errorData.exception_class as string | undefined;
      if (exceptionClass && !message.includes(exceptionClass)) {
        message = `${message} (${exceptionClass})`;
      }
      return new ApiRequestError(
        message,
        status,
        (errorData.code as string) || (errorData.error as string) || undefined,
        errorData.details as Record<string, unknown> | undefined,
      );
    }
    return new ApiRequestError('Request failed', status);
  }
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  action: string;
  userId: number;
  userName: string;
  organisationId: number;
  organisationName: string;
  resourceType: string;
  resourceId: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
}

export interface DataSharingInfo {
  isShared: boolean;
  sharedWith: Array<{
    organisationId: number;
    organisationName: string;
    accessLevel: 'read' | 'write' | 'admin';
    grantedAt: string;
    grantedBy: string;
    expiresAt?: string;
  }>;
  sharedFrom?: {
    organisationId: number;
    organisationName: string;
    accessLevel: 'read' | 'write' | 'admin';
  };
}
